import { yupResolver } from '@hookform/resolvers/yup';
import { UserCircleIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react-native';
import {
  useAcceptTerms,
  useGetProfile,
  useUpdateProfile,
  useUpdateProfilePhoto,
} from '@sunrise-ui/api-client';
import type {
  GenderPronouns,
  UpdateProfileInput,
} from '@sunrise-ui/api/profile';
import {
  AuthFlowError,
  Button,
  ButtonVariants,
  Card,
  Colors,
  Dropdown,
  InputError,
  Loader,
  ProfileBasicInfo,
  TermsOfService,
  TextInput,
  TextReg,
  useAuthStore,
  useDynamicStyles,
  useImagePicker,
  usePushNotifications,
  useSnackbar,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import type { ImagePickerAsset } from 'expo-image-picker';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Image,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { Checkbox } from 'react-native-paper';
import { boolean, object, string, type InferType } from 'yup';

const schema = object().shape({
  firstName: string()
    .required('Required')
    .max(20, 'Must be 20 characters or less'),
  lastName: string()
    .required('Required')
    .max(20, 'Must be 20 characters or less'),
  pronouns: string(),
  headline: string()
    .required('Required')
    .max(40, 'Must be 40 characters or less'),
  hasAcceptedTerms: boolean().when('$needsToAcceptTerms', {
    is: true,
    then: (schema) =>
      schema
        .oneOf([true], 'You must accept our terms to continue')
        .required('You must accept our terms to continue'),
  }),
});

type ProfileFormValues = InferType<typeof schema>;

export function ProfileSetupModalScreen() {
  const styles = useDynamicStyles(createStyles);
  const pickImage = useImagePicker();
  const { showSnackbar } = useSnackbar();
  const { requestPermission } = usePushNotifications();
  const { currentUserId, hasAcceptedTerms } = useAuthStore();

  const { data: existingProfile, isLoading: isLoadingExistingProfile } =
    useGetProfile(currentUserId);
  const [isFormReady, setIsFormReady] = useState(!isLoadingExistingProfile);
  const [existingProfileImageUri, setExistingProfileImageUri] =
    useState<string>();
  const [image, setImage] = useState<ImagePickerAsset>();

  useEffect(() => {
    void requestPermission();
  }, []);

  const { mutateAsync: updateProfile } = useUpdateProfile();
  const { mutateAsync: updateProfilePhoto } = useUpdateProfilePhoto();
  const { mutateAsync: acceptTerms } = useAcceptTerms();

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isSubmitted, isValid },
    watch,
    reset,
  } = useForm<ProfileFormValues>({
    defaultValues: {
      firstName: existingProfile?.first_name ?? '',
      lastName: existingProfile?.last_name ?? '',
      pronouns: existingProfile?.pronouns ?? '',
      headline: existingProfile?.headline ?? '',
      hasAcceptedTerms: hasAcceptedTerms,
    },
    resolver: yupResolver(schema),
    context: { needsToAcceptTerms: !hasAcceptedTerms },
  });

  useEffect(() => {
    if (!isLoadingExistingProfile) {
      reset({
        firstName: existingProfile?.first_name ?? '',
        lastName: existingProfile?.last_name ?? '',
        pronouns: existingProfile?.pronouns ?? '',
        headline: existingProfile?.headline ?? '',
        hasAcceptedTerms: hasAcceptedTerms,
      });
      setExistingProfileImageUri(existingProfile?.photos?.small);
      setIsFormReady(true);
    }
  }, [isLoadingExistingProfile]);

  const handlePickImage = async () => {
    const img = await pickImage();
    if (img) {
      setImage(img);
    }
  };

  const saveProfile = async (data: ProfileFormValues) => {
    if (!data || (!image && !existingProfileImageUri) || !currentUserId) return;

    try {
      const { firstName, lastName, pronouns, headline } = data;
      const updateProfileInput: UpdateProfileInput = {
        first_name: firstName,
        last_name: lastName,
        headline: headline,
        user_id: currentUserId,
      };
      if (pronouns) updateProfileInput.pronouns = pronouns as GenderPronouns;

      if (!hasAcceptedTerms) {
        await acceptTerms();
      }

      if (image?.file) {
        await Promise.all([
          updateProfile(updateProfileInput),
          updateProfilePhoto({ file: image.file, userId: currentUserId }),
        ]);
      } else {
        await updateProfile(updateProfileInput);
      }
    } catch (e) {
      if (e instanceof AuthFlowError) return;
      showSnackbar({
        text: 'Profile failed to save. Please try again.',
        type: 'error',
      });
    }
  };

  const imageUri = image?.uri || existingProfileImageUri;

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={styles.flex}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
    >
      <ScrollView
        style={styles.scrollContainer}
        contentContainerStyle={styles.scrollContent}
        keyboardDismissMode="interactive"
      >
        <TextReg style={styles.subtitle}>
          Let&apos;s fill out your profile. This is how other attendees will see
          you.
        </TextReg>
        <View style={styles.infoPreview}>
          {imageUri ? (
            <Image
              source={{ uri: imageUri }}
              aria-label="Your Profile Photo Preview"
              style={styles.circle}
            />
          ) : (
            <View style={styles.circle}>
              <HugeiconsIcon
                icon={UserCircleIcon}
                color={Colors.text}
                size={40}
                strokeWidth={0.5}
              />
            </View>
          )}
          <ProfileBasicInfo
            firstName={watch('firstName')}
            lastName={watch('lastName')}
            pronouns={watch('pronouns')}
            headline={watch('headline')}
            size={'small'}
            style={styles.profileBasicInfo}
          />
        </View>
        <Card style={styles.card} shadowColor={Colors.purple1}>
          {!isFormReady && (
            <View style={styles.cardLoader}>
              <Loader />
            </View>
          )}
          {isFormReady && (
            <>
              <View style={styles.inputsRow}>
                <View style={styles.rowInput}>
                  <Controller
                    control={control}
                    render={({ field }) => (
                      <TextInput
                        label="First Name*"
                        {...field}
                        maxLength={20}
                        dense={true}
                      />
                    )}
                    name="firstName"
                  />
                  <InputError size="small" fieldError={errors.firstName} />
                </View>
                <View style={styles.rowInput}>
                  <Controller
                    control={control}
                    render={({ field }) => (
                      <TextInput
                        label="Last Name*"
                        {...field}
                        maxLength={20}
                        dense={true}
                      />
                    )}
                    name="lastName"
                  />
                  <InputError size="small" fieldError={errors.lastName} />
                </View>
              </View>
              <View style={styles.headline}>
                <Controller
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      label="Headline*"
                      maxLength={40}
                      {...field}
                      dense={true}
                    />
                  )}
                  name="headline"
                />
                <InputError size="small" fieldError={errors.headline} />
              </View>
              <View style={styles.inputsRow}>
                <View style={styles.rowInput}>
                  <Controller
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <Dropdown
                        label="Pronouns"
                        placeholder="Select"
                        options={[
                          { label: 'he/him', value: 'he/him' },
                          { label: 'she/her', value: 'she/her' },
                          { label: 'they/them', value: 'they/them' },
                        ]}
                        value={value}
                        onChange={onChange}
                        onClear={() => onChange('')}
                      />
                    )}
                    name="pronouns"
                  />
                  <InputError size="small" fieldError={errors.pronouns} />
                </View>
                <View style={[styles.rowInput, styles.uploadButtonContainer]}>
                  <Button
                    variant={ButtonVariants.WHITE}
                    size="medium"
                    onPress={handlePickImage}
                    contentStyle={{ height: 40 }}
                  >
                    Upload Headshot
                  </Button>
                  <InputError
                    size="small"
                    fieldError={
                      isSubmitted && !image && !existingProfileImageUri
                        ? { message: 'Headshot is required' }
                        : undefined
                    }
                  />
                </View>
              </View>
            </>
          )}
        </Card>
        {!hasAcceptedTerms && (
          <Controller
            control={control}
            name="hasAcceptedTerms"
            render={({ field: { value, onChange } }) => (
              <View>
                <View style={styles.termsRow}>
                  <Checkbox.Android
                    status={value ? 'checked' : 'unchecked'}
                    onPress={() => {
                      onChange(!value);
                    }}
                  />
                  <TermsOfService />
                </View>
                <InputError fieldError={errors.hasAcceptedTerms} />
              </View>
            )}
          />
        )}
      </ScrollView>
      <View style={styles.footer}>
        <Button
          style={styles.saveButton}
          variant={ButtonVariants.PURPLE}
          size={'medium'}
          disabled={!isValid || isSubmitting}
          loading={isSubmitting}
          onPress={handleSubmit(saveProfile)}
        >
          Finish
        </Button>
      </View>
    </KeyboardAvoidingView>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    flex: {
      flex: 1,
      width: '100%',
    },
    scrollContainer: {
      flex: 1,
    },
    scrollContent: {
      gap: isMobile ? 16 : 20,
      alignItems: 'center',
      width: '100%',
      paddingHorizontal: 12,
      marginTop: isMobile ? 16 : 8,
    },
    subtitle: {
      textAlign: 'center',
      fontSize: isMobile ? 14 : 16,
    },
    infoPreview: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 12,
      maxWidth: 500,
    },
    profileBasicInfo: {
      flexGrow: 0,
    },
    circle: {
      width: 40,
      height: 40,
      borderRadius: 40,
    },
    card: {
      width: '100%',
      maxWidth: 550,
      gap: 16,
      padding: isMobile ? 20 : 24,
    },
    inputsRow: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: 16,
      justifyContent: 'space-between',
      width: '100%',
    },
    rowInput: {
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: '50%',
    },
    uploadButtonContainer: {
      justifyContent: 'flex-end',
    },
    headline: {
      width: '100%',
    },
    saveButton: {
      width: isMobile ? 120 : 150,
    },
    cardLoader: {
      height: isMobile ? 447 : 293,
    },
    termsTextContainer: {
      position: 'relative',
      flexShrink: 1,
    },
    termsRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      flex: 1,
      justifyContent: 'flex-end',
    },
    footer: {
      maxWidth: '100%',
      padding: 12,
      paddingBottom: isMobile ? 36 : 12,
      gap: 16,
      alignItems: 'center',
      flexDirection: 'row',
      justifyContent: 'center',
    },
    errorText: {
      fontSize: 10,
      position: 'absolute',
      bottom: -16,
    },
  });
